// ErrorFallback.js
import React from 'react';
import { RefreshCw, AlertTriangle, Home, ArrowLeft } from 'lucide-react';

const ErrorFallback = ({
  error,
  resetErrorBoundary,
  title = "Something went wrong",
  message = "An unexpected error occurred. Please try again.",
  showHomeButton = true,
  showBackButton = true,
  variant = 'default'
}) => {
  // Only show error details in development (localhost)
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                        window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const variants = {
    default: {
      container: 'bg-white',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-gray-900',
      messageColor: 'text-gray-600'
    },
    minimal: {
      container: 'bg-transparent',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
      titleColor: 'text-gray-800',
      messageColor: 'text-gray-500'
    },
    dark: {
      container: 'bg-gray-800',
      iconBg: 'bg-red-900',
      iconColor: 'text-red-400',
      titleColor: 'text-white',
      messageColor: 'text-gray-300'
    }
  };

  const currentVariant = variants[variant] || variants.default;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className={`max-w-lg w-full ${currentVariant.container} rounded-lg shadow-lg p-8 text-center`}>
        {/* Error Icon */}
        <div className={`w-20 h-20 mx-auto mb-6 ${currentVariant.iconBg} rounded-full flex items-center justify-center`}>
          <AlertTriangle className={`w-10 h-10 ${currentVariant.iconColor}`} />
        </div>

        {/* Title */}
        <h1 className={`text-3xl font-bold ${currentVariant.titleColor} mb-4`}>
          {title}
        </h1>

        {/* Message */}
        <p className={`text-lg ${currentVariant.messageColor} mb-8 leading-relaxed`}>
          {message}
        </p>

        {/* Action Buttons */}
        <div className="space-y-4">
          <button
            onClick={resetErrorBoundary}
            className="w-full flex items-center justify-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Try Again</span>
          </button>

          <div className="flex space-x-3">
            {showBackButton && (
              <button
                onClick={handleGoBack}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Go Back</span>
              </button>
            )}

            {showHomeButton && (
              <button
                onClick={handleGoHome}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </button>
            )}
          </div>

          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Refresh Page
          </button>
        </div>

        {/* Error Details (ONLY in Development/Localhost) */}
        {isDevelopment && error && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 font-medium">
              Error Details (Development Mode Only)
            </summary>
            <div className="mt-4 p-4 bg-gray-100 rounded-md text-left">
              <h4 className="font-medium text-gray-900 mb-2">Error Message:</h4>
              <p className="text-sm text-red-600 mb-4 font-mono bg-red-50 p-2 rounded">
                {error.message}
              </p>
              
              {error.stack && (
                <>
                  <h4 className="font-medium text-gray-900 mb-2">Stack Trace:</h4>
                  <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-auto max-h-40 whitespace-pre-wrap">
                    {error.stack}
                  </pre>
                </>
              )}
            </div>
          </details>
        )}

        {/* Support Information */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Need help? Contact our support team at{' '}
            <a 
              href="mailto:support@medigo.co.ke" 
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              support@medigo.co.ke
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Specialized error fallbacks for different scenarios
export const NetworkErrorFallback = (props) => (
  <ErrorFallback
    {...props}
    title="Connection Error"
    message="Unable to connect to our servers. Please check your internet connection and try again."
  />
);

export const NotFoundErrorFallback = (props) => (
  <ErrorFallback
    {...props}
    title="Page Not Found"
    message="The page you're looking for doesn't exist or has been moved."
    showBackButton={true}
    showHomeButton={true}
  />
);

export const AuthErrorFallback = (props) => (
  <ErrorFallback
    {...props}
    title="Authentication Error"
    message="Your session has expired. Please log in again to continue."
    showBackButton={false}
  />
);

export const PermissionErrorFallback = (props) => (
  <ErrorFallback
    {...props}
    title="Access Denied"
    message="You don't have permission to access this page."
    showBackButton={true}
    showHomeButton={true}
  />
);

export default ErrorFallback;